import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useJornada } from '@/contexts/JornadaContext';
import { UtensilsCrossed, Loader2, LogOut, LogIn, Flag, ChevronDown, Clock, Send, CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { formatarErroAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

const toSec = (h) => {
  if (!h) return null;
  const [hh, mm, ss = '0'] = String(h).split(':');
  return Number(hh) * 3600 + Number(mm) * 60 + Number(ss);
};
const hhmm = (v) => (v ? String(v).slice(0, 5) : '--:--');
const pad = (n) => String(n).padStart(2, '0');
export const fmtCrono = (seg) => `${pad(Math.floor(seg / 3600))}:${pad(Math.floor((seg % 3600) / 60))}:${pad(Math.floor(seg % 60))}`;
export const fmtHoras = (seg) => `${Math.floor(seg / 3600)}h${pad(Math.floor((seg % 3600) / 60))}min`;

// Mesma regra do backend: soma das sessões login→logout, descontando o almoço que cair dentro delas
export const calcularSegundos = (j, agoraSeg) => {
  const sessoes = j.sessoes?.length ? j.sessoes : [{ login: j.login_hora, logout: j.logout_hora }];
  const ai = toSec(j.almoco_inicio);
  let ar = toSec(j.almoco_retorno);
  if (ai !== null && ar === null) ar = agoraSeg;
  let total = 0;
  let desconto = 0;
  for (const s of sessoes) {
    const ini = toSec(s.login);
    const fim = s.logout ? toSec(s.logout) : agoraSeg;
    if (ini === null || fim === null || fim <= ini) continue;
    total += fim - ini;
    if (ai !== null && ar !== null) desconto += Math.max(0, Math.min(fim, ar) - Math.max(ini, ai));
  }
  const almoco = ai !== null && ar !== null && ar > ai ? ar - ai : 0;
  return { trabalhado: Math.max(0, total - desconto), almoco };
};

const ESTADOS = {
  ativo: { label: 'Trabalhando', dot: 'bg-emerald-500', ping: 'bg-emerald-400', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-300/60 dark:border-emerald-700/50', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
  almoco: { label: 'Almoço', dot: 'bg-orange-500', ping: 'bg-orange-400', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-300/60 dark:border-orange-700/50', bg: 'bg-orange-50 dark:bg-orange-900/20' },
  encerrado: { label: 'Encerrado', dot: 'bg-gray-400', ping: '', text: 'text-gray-600 dark:text-gray-300', border: 'border-gray-300 dark:border-gray-700', bg: 'bg-gray-50 dark:bg-gray-800/40' },
};

// Hora extra: qualquer colaborador (exceto admin) pede pelo topo; admin aprova no painel flutuante
const HoraExtraControle = ({ horaExtra, jornadaSaida, solicitar }) => {
  const [aberto, setAberto] = useState(false);
  const [horas, setHoras] = useState('1');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const enviar = async () => {
    setEnviando(true);
    try {
      const r = await solicitar({ horas: parseFloat(horas) || 1, motivo });
      toast.success(r.message || 'Solicitação enviada aos administradores');
      setAberto(false);
      setMotivo('');
    } catch (e) {
      toast.error(formatarErroAPI(e));
    } finally {
      setEnviando(false);
    }
  };

  if (horaExtra?.status === 'pendente') {
    return (
      <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 border border-amber-300/60" title={`Pedido de ${horaExtra.horas_solicitadas || 1}h aguardando aprovação do administrador`} data-testid="hora-extra-status-pendente">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> <span className="hidden sm:inline">Hora extra: aguardando</span>
      </span>
    );
  }
  if (horaExtra?.status === 'aprovada') {
    return (
      <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 border border-emerald-300/60" title={`Aprovada por ${horaExtra.admin_nome || 'admin'}`} data-testid="hora-extra-status-aprovada">
        <CheckCircle2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Extra até {horaExtra.hora_limite}</span><span className="sm:hidden">{horaExtra.hora_limite}</span>
      </span>
    );
  }
  const negada = horaExtra?.status === 'negada';
  return (
    <>
      <button
        onClick={() => setAberto(true)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all active:scale-95 ${negada ? 'border-red-300 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20' : 'border-purple-300 text-purple-700 dark:text-purple-200 dark:border-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/30'}`}
        title={negada ? `Negada por ${horaExtra.admin_nome || 'admin'} — clique para solicitar novamente` : 'Solicitar hora extra para hoje'}
        data-testid="hora-extra-button"
      >
        {negada ? <XCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">{negada ? 'Extra negada · pedir de novo' : 'Hora extra'}</span>
      </button>
      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent className="max-w-sm" data-testid="hora-extra-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clock className="w-5 h-5 text-purple-600" /> Solicitar hora extra</DialogTitle>
            <DialogDescription>
              {jornadaSaida ? `Sua saída prevista é às ${jornadaSaida}. ` : ''}Seu supervisor, gerente ou o administrador recebe o pedido na hora e você é avisado quando for aprovado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium mb-1.5">Quantas horas?</p>
              <div className="flex gap-2">
                {['0.5', '1', '2', '3'].map((h) => (
                  <Button key={h} size="sm" variant={horas === h ? 'default' : 'outline'} className={horas === h ? 'bg-purple-600 hover:bg-purple-700' : ''} onClick={() => setHoras(h)} data-testid={`hora-extra-opcao-${h}`}>{h}h</Button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium mb-1.5">Motivo (opcional)</p>
              <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} maxLength={300} placeholder="Ex.: finalizar cadastros do mailing" className="w-full rounded-md border bg-background px-3 py-2 text-sm" data-testid="hora-extra-motivo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAberto(false)} disabled={enviando}>Cancelar</Button>
            <Button onClick={enviar} disabled={enviando} className="bg-purple-600 hover:bg-purple-700" data-testid="hora-extra-enviar">
              {enviando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Enviar pedido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const JornadaHeaderBar = ({ onSair, saindo }) => {
  const { user } = useAuth();
  const { jornadaHoje, relogio, iniciarPausaAlmoco, horaExtra, jornadaSaida, solicitarHoraExtra, ocultarCronometro, alternarCronometro } = useJornada();
  const [agoraSeg, setAgoraSeg] = useState(null);
  const [pausando, setPausando] = useState(false);
  const [alternando, setAlternando] = useState(false);
  const [aberto, setAberto] = useState(false);
  const painelRef = useRef(null);

  useEffect(() => {
    if (!relogio) return;
    const tick = () => setAgoraSeg(relogio.servidorSeg + (Date.now() - relogio.clienteMs) / 1000);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [relogio]);

  useEffect(() => {
    if (!aberto) return;
    const fechar = (e) => { if (painelRef.current && !painelRef.current.contains(e.target)) setAberto(false); };
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, [aberto]);

  if (!jornadaHoje || agoraSeg === null) return null;

  const status = jornadaHoje.status || 'ativo';
  const estado = ESTADOS[status] || ESTADOS.ativo;
  const { trabalhado, almoco } = calcularSegundos(jornadaHoje, agoraSeg);
  const encerrada = status === 'encerrado';
  const podePausar = !jornadaHoje.almoco_inicio && !encerrada;
  const sessoes = jornadaHoje.sessoes?.length ? jornadaHoje.sessoes : [{ login: jornadaHoje.login_hora, logout: jornadaHoje.logout_hora }];

  const handlePausar = async (e) => {
    e.stopPropagation();
    setPausando(true);
    try {
      await iniciarPausaAlmoco();
      toast.success('Pausa de almoço iniciada — bom apetite!');
    } catch (err) {
      toast.error(formatarErroAPI(err));
    } finally {
      setPausando(false);
    }
  };

  const handleAlternarCronometro = async () => {
    setAlternando(true);
    try {
      const r = await alternarCronometro(!ocultarCronometro);
      toast.success(r.message);
    } catch (err) {
      toast.error(formatarErroAPI(err));
    } finally {
      setAlternando(false);
    }
  };

  const timeline = [
    { icone: LogIn, label: 'Entrada', hora: sessoes[0]?.login, cor: 'text-emerald-500' },
    { icone: UtensilsCrossed, label: 'Almoço', hora: jornadaHoje.almoco_inicio, cor: 'text-orange-500' },
    { icone: UtensilsCrossed, label: 'Retorno', hora: jornadaHoje.almoco_retorno, cor: 'text-orange-500' },
    { icone: Flag, label: 'Saída', hora: jornadaHoje.logout_hora, cor: 'text-red-500' },
  ];

  return (
    <div className="relative flex items-center gap-2" ref={painelRef} data-testid="jornada-header-bar">
      <button
        onClick={() => setAberto(!aberto)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${estado.border} ${estado.bg} transition-colors hover:brightness-95`}
        data-testid="jornada-status-pill"
        title="Ver linha do tempo do dia"
      >
        <span className="relative flex h-2.5 w-2.5">
          {estado.ping && <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${estado.ping} opacity-75`} />}
          <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${estado.dot}`} />
        </span>
        <span className={`${ocultarCronometro ? 'inline' : 'hidden'} sm:inline text-xs font-semibold ${estado.text}`} data-testid="jornada-status-label">{estado.label}</span>
        {!ocultarCronometro && (
          <span className={`font-mono text-sm tabular-nums font-bold ${estado.text}`} data-testid="jornada-cronometro">
            {encerrada ? fmtHoras(trabalhado) : fmtCrono(trabalhado)}
          </span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 ${estado.text} transition-transform ${aberto ? 'rotate-180' : ''}`} />
      </button>

      {podePausar && (
        <button
          onClick={handlePausar}
          disabled={pausando}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 active:scale-95 transition-all disabled:opacity-60"
          data-testid="pausa-almoco-button"
          title="Registrar início do almoço"
        >
          {pausando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UtensilsCrossed className="w-3.5 h-3.5" />}
          <span className="hidden sm:inline">Pausa almoço</span>
        </button>
      )}

      {user?.perfil !== 'ADMIN' && !encerrada && (
        <HoraExtraControle horaExtra={horaExtra} jornadaSaida={jornadaSaida} solicitar={solicitarHoraExtra} />
      )}

      <button
        onClick={onSair}
        disabled={saindo}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-red-600 text-white hover:bg-red-700 active:scale-95 transition-all disabled:opacity-60"
        data-testid="header-sair-button"
        title="Registrar saída e fechar o CRM"
      >
        {saindo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">{saindo ? 'Registrando...' : 'Sair'}</span>
      </button>

      {aberto && (
        <div className="absolute top-11 right-0 w-72 bg-white dark:bg-[#120525] border border-gray-200 dark:border-purple-900/40 rounded-xl shadow-2xl z-50 p-4" data-testid="jornada-painel-dia">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">Jornada de hoje</span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${estado.bg} ${estado.text}`}>{estado.label}</span>
          </div>
          <ol className="relative border-l border-gray-200 dark:border-purple-900/50 ml-2 space-y-3">
            {timeline.map((t) => (
              <li key={t.label} className="ml-4">
                <span className={`absolute -left-[7px] mt-1 w-3 h-3 rounded-full border-2 border-white dark:border-[#120525] ${t.hora ? t.cor.replace('text-', 'bg-') : 'bg-gray-300 dark:bg-gray-600'}`} />
                <div className="flex items-center justify-between">
                  <span className={`flex items-center gap-1.5 text-xs ${t.hora ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400'}`}>
                    <t.icone className={`w-3.5 h-3.5 ${t.hora ? t.cor : 'text-gray-400'}`} /> {t.label}
                  </span>
                  <span className={`font-mono text-sm ${t.hora ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-400'}`} data-testid={`timeline-${t.label.toLowerCase()}`}>{hhmm(t.hora)}</span>
                </div>
              </li>
            ))}
          </ol>
          {sessoes.length > 1 && (
            <p className="mt-3 text-[11px] text-purple-600 dark:text-purple-300" data-testid="timeline-sessoes">
              {sessoes.length} sessões hoje: {sessoes.map((s) => `${hhmm(s.login)}–${hhmm(s.logout)}`).join(' · ')}
            </p>
          )}
          <div className={`mt-4 grid ${ocultarCronometro ? 'grid-cols-1' : 'grid-cols-2'} gap-2`}>
            {!ocultarCronometro && (
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-2 text-center">
                <p className="text-[10px] text-emerald-700 dark:text-emerald-300">Trabalhado</p>
                <p className="font-mono font-bold text-emerald-800 dark:text-emerald-200" data-testid="painel-trabalhado">{fmtHoras(trabalhado)}</p>
              </div>
            )}
            <div className="rounded-lg bg-orange-50 dark:bg-orange-900/20 p-2 text-center">
              <p className="text-[10px] text-orange-700 dark:text-orange-300">Almoço</p>
              <p className="font-mono font-bold text-orange-800 dark:text-orange-200" data-testid="painel-almoco">{fmtHoras(almoco)}</p>
            </div>
          </div>
          <button
            onClick={handleAlternarCronometro}
            disabled={alternando}
            className="mt-3 w-full flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 dark:border-purple-800/60 px-2 py-1.5 text-[11px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-purple-900/20 transition-colors disabled:opacity-60"
            title={ocultarCronometro ? 'Voltar a mostrar o tempo trabalhado no topo' : 'Esconder o tempo trabalhado no topo (as horas continuam sendo registradas)'}
            data-testid="alternar-cronometro-button"
          >
            {alternando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : ocultarCronometro ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            {ocultarCronometro ? 'Mostrar cronômetro' : 'Ocultar cronômetro'}
          </button>
        </div>
      )}
    </div>
  );
};

export default JornadaHeaderBar;
